import { PageTemplate } from "@/common/PageWrapper";
import { DoneDataHandler, ErrorDataHandler, LoadingDataHandler, RoutedDBProvider } from "@/providers/FirebaseProvider";
import { useAuth, useDialogController } from "@/GlobalData";
import { User } from "@/common/data/User";
import M3ripple from "@/common/components/m3ripple";
import { M3InfiniteLoading } from "@/common/components/m3infiniteloading";
import { loadDateFromISOString } from "@/common/utils/DateTools";
import { ChangeName } from "./dialog/ChangeName";
import { ChangeBirthday } from "./dialog/ChangeBirthday";
import { ChangeGender } from "./dialog/ChangeGender";
import { Link } from "react-router";
import { SignOut } from "./dialog/SignOut";

export function Loading() {
    return <div className="w-full h-96 p-2.5 bg-white inline-flex flex-col justify-center items-center gap-2.5 overflow-hidden">
        <M3InfiniteLoading />
        <div className="justify-start text-black text-2xl font-semibold font-['Be_Vietnam_Pro'] leading-10">Loading data</div>
        <div className="justify-start text-black text-sm font-semibold font-['Be_Vietnam_Pro'] leading-tight">This should only take a moment.</div>
    </div>
}

export function Error() {
    return <div className="min-h-screen flex flex-col">
        <main className="flex-1">

            <div className="w-full h-96 p-2.5 bg-white inline-flex flex-col justify-center items-center gap-2.5 overflow-hidden">
                <div className="size-20 relative overflow-hidden">
                    <div data-svg-wrapper className="left-0 top-0 absolute">
                        <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M40 80C62.0914 80 80 62.0914 80 40C80 17.9086 62.0914 0 40 0C17.9086 0 0 17.9086 0 40C0 62.0914 17.9086 80 40 80Z" fill="#FF4657" />
                        </svg>
                    </div>
                    <div data-svg-wrapper className="left-[18.74px] top-[19.06px] absolute">
                        <svg width="44" height="42" viewBox="0 0 44 42" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M0.904634 10.3413L10.1331 1.11281C10.3544 0.891489 10.7125 0.891489 10.9339 1.11281L20.6274 10.8063C20.8487 11.0276 21.2068 11.0276 21.4281 10.8063L32.0069 0.227517C32.2282 0.00619239 32.5863 0.00619239 32.8076 0.227517L42.5161 9.93594C42.7374 10.1573 42.7374 10.5154 42.5161 10.7367L33.3025 19.9502C33.0812 20.1716 33.0812 20.5297 33.3025 20.751L43.0955 30.544C43.3168 30.7653 43.3168 31.1234 43.0955 31.3447L32.6659 41.7743C32.4446 41.9956 32.0865 41.9956 31.8652 41.7743L21.438 31.3472C21.2167 31.1259 20.8586 31.1259 20.6373 31.3472L11.0731 40.9114C10.8518 41.1327 10.4937 41.1327 10.2724 40.9114L1.34977 31.9888C1.12845 31.7675 1.12845 31.4094 1.34977 31.188L10.6827 21.8551C10.904 21.6338 10.904 21.2782 10.6852 21.0569L0.902148 11.1395C0.683311 10.9182 0.685796 10.5601 0.904634 10.3413Z" fill="white" />
                        </svg>
                    </div>
                </div>
                <div className="justify-start text-black text-2xl font-semibold font-['Be_Vietnam_Pro'] leading-10">You are not logged in.</div>
                <div className="justify-start text-black text-sm font-semibold font-['Be_Vietnam_Pro'] leading-tight">Please log in to view this page</div>
                <Link className="w-full flex flex-rot items-center justify-center"
                    to={"/login"}
                >
                    <button className="w-[80%] h-12 bg-teal-900 rounded-[10px] inline-flex justify-center items-center gap-2.5">
                        <div className="text-center justify-start text-neutral-50 text-base font-normal font-['Be_Vietnam_Pro'] leading-normal">Login page</div>
                    </button>
                </Link>
            </div>
        </main>
        <footer
            className="h-[160px] bg-bottom bg-repeat-x"
            style={{
                backgroundImage: `url(${new URL('~/src/assets/pattern.png', import.meta.url).href})`,
                backgroundSize: 'auto',
            }}
        />
    </div>
}

export function ProfileContent({ user, dialogcontroller, modify }: { user: User | null, dialogcontroller: ReturnType<typeof useDialogController>, modify: (modifier: (draft: User) => void) => Promise<User> }) {
    if (user == null) {
        return <Loading />
    }

    const date = loadDateFromISOString(user.ulangTahun) ?? new Date()
    return <div className="min-h-screen flex flex-col">
        <main className="flex-1">

            <div className="w-full p-2.5 inline-flex flex-col justify-start items-start gap-2.5 overflow-hidden">

                <div className="self-stretch rounded-lg outline outline-1 outline-offset-[-1px] outline-zinc-100 flex flex-col justify-center items-start">
                    <div className="self-stretch px-7 py-6 inline-flex justify-start items-center gap-2.5">
                        <div className="w-52 justify-start text-zinc-800 text-2xl font-semibold font-['Be_Vietnam_Pro'] leading-9">Informasi Dasar</div>
                    </div>
                    <div className="self-stretch p-4 border-t border-zinc-100 inline-flex justify-start items-center gap-2.5 overflow-hidden relative">
                        <div className="justify-center text-stone-500 text-sm font-bold font-['Be_Vietnam_Pro'] leading-tight">Foto Profil</div>
                        <div className="flex-1 self-stretch bg-white flex justify-center items-center overflow-hidden">
                            <div className="w-20 relative bg-stone-300 rounded-[69px] aspect-square" />
                        </div>
                        <div data-svg-wrapper className="relative">
                            <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M5 21.5C4.45 21.5 3.97917 21.3042 3.5875 20.9125C3.19583 20.5208 3 20.05 3 19.5V5.5C3 4.95 3.19583 4.47917 3.5875 4.0875C3.97917 3.69583 4.45 3.5 5 3.5H12V5.5H5V19.5H19V12.5H21V19.5C21 20.05 20.8042 20.5208 20.4125 20.9125C20.0208 21.3042 19.55 21.5 19 21.5H5ZM9.7 16.2L8.3 14.8L17.6 5.5H14V3.5H21V10.5H19V6.9L9.7 16.2Z" fill="black" />
                            </svg>
                        </div>
                        <M3ripple />
                    </div>
                    <button className="w-full relative" onClick={() => {
                        dialogcontroller.showDialog(() => <ChangeName oldname={user.namaLengkap} modify={modify} />, [], true, "Ganti Nama")
                    }}>

                        <div className="w-full self-stretch p-4 border-t border-zinc-100 inline-flex justify-start items-center gap-2.5 overflow-hidden">
                            <div className="justify-start text-stone-500 text-sm font-bold font-['Be_Vietnam_Pro'] leading-tight">Nama</div>
                            <div className="flex-1 p-2.5 flex justify-center items-center gap-2.5 overflow-hidden">
                                <div className="justify-start text-stone-500 text-sm font-bold font-['Be_Vietnam_Pro'] leading-tight">{user.namaLengkap}</div>
                            </div>
                            <div data-svg-wrapper className="relative">
                                <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M8.025 22.5L6.25 20.725L14.475 12.5L6.25 4.275L8.025 2.5L18.025 12.5L8.025 22.5Z" fill="black" />
                                </svg>
                            </div>

                            <M3ripple />
                        </div>
                    </button>
                    <button className="self-stretch p-4 border-t border-zinc-100 inline-flex justify-start items-center gap-2.5 overflow-hidden relative" onClick={() => {
                        dialogcontroller.showDialog(() => <ChangeBirthday oldbirthday={user.ulangTahun} modify={modify} />, [], true, "Ganti Ulang Tahun")
                    }}>
                        <div className="justify-start text-stone-500 text-sm font-bold font-['Be_Vietnam_Pro'] leading-tight">Ulang Tahun</div>
                        <div className="flex-1 p-2.5 flex justify-center items-center gap-2.5 overflow-hidden">
                            <div className="justify-start text-stone-500 text-sm font-bold font-['Be_Vietnam_Pro'] leading-tight">{`${date.getDate()}-${date.getMonth()}-${date.getFullYear()}`}</div>
                        </div>
                        <div data-svg-wrapper className="relative">
                            <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M8.025 22.5L6.25 20.725L14.475 12.5L6.25 4.275L8.025 2.5L18.025 12.5L8.025 22.5Z" fill="black" />
                            </svg>
                        </div>

                        <M3ripple />
                    </button>
                    <button className="self-stretch p-4 border-t border-zinc-100 inline-flex justify-start items-center gap-2.5 overflow-hidden relative" onClick={() => {
                        dialogcontroller.showDialog(() => <ChangeGender oldgender={user.kelamin} modify={modify} />, [], true, "Ganti Kelamin")
                    }}>
                        <div className="justify-start text-stone-500 text-sm font-bold font-['Be_Vietnam_Pro'] leading-tight">Kelamin</div>
                        <div className="flex-1 p-2.5 flex justify-center items-center gap-2.5 overflow-hidden">
                            <div className="justify-start text-stone-500 text-sm font-bold font-['Be_Vietnam_Pro'] leading-tight">{user.kelamin == "lakilaki" ? "Laki-Laki" : "Perempuan"}</div>
                        </div>
                        <div data-svg-wrapper className="relative">
                            <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M8.025 22.5L6.25 20.725L14.475 12.5L6.25 4.275L8.025 2.5L18.025 12.5L8.025 22.5Z" fill="black" />
                            </svg>
                        </div>

                        <M3ripple />
                    </button>
                </div>
                <div className="self-stretch rounded-lg outline outline-1 outline-offset-[-1px] outline-zinc-100 flex flex-col justify-center items-start">
                    <div className="self-stretch px-7 py-6 inline-flex justify-start items-center gap-2.5">
                        <div className="w-52 justify-start text-zinc-800 text-2xl font-semibold font-['Be_Vietnam_Pro'] leading-9">Contact information</div>
                    </div>
                    <div className="self-stretch p-4 border-t border-zinc-100 inline-flex justify-start items-center gap-2.5 overflow-hidden relative">
                        <div className="justify-start text-stone-500 text-sm font-bold font-['Be_Vietnam_Pro'] leading-tight">Alamat Email</div>
                        <div className="flex-1 p-2.5 flex justify-center items-center gap-2.5 overflow-hidden">
                            <div className="justify-start text-stone-500 text-sm font-bold font-['Be_Vietnam_Pro'] leading-tight">{user.email}</div>
                        </div>
                        <div data-svg-wrapper className="relative">
                            <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M9 18.5C8.45 18.5 7.97917 18.3042 7.5875 17.9125C7.19583 17.5208 7 17.05 7 16.5V4.5C7 3.95 7.19583 3.47917 7.5875 3.0875C7.97917 2.69583 8.45 2.5 9 2.5H18C18.55 2.5 19.0208 2.69583 19.4125 3.0875C19.8042 3.47917 20 3.95 20 4.5V16.5C20 17.05 19.8042 17.5208 19.4125 17.9125C19.0208 18.3042 18.55 18.5 18 18.5H9ZM9 16.5H18V4.5H9V16.5ZM5 22.5C4.45 22.5 3.97917 22.3042 3.5875 21.9125C3.19583 21.5208 3 21.05 3 20.5V6.5H5V20.5H16V22.5H5Z" fill="black" />
                            </svg>
                        </div>

                        <M3ripple />
                    </div>
                    <div className="self-stretch p-4 border-t border-zinc-100 inline-flex justify-start items-center gap-2.5 overflow-hidden relative">
                        <div className="justify-start text-stone-500 text-sm font-bold font-['Be_Vietnam_Pro'] leading-tight">Nomor Telepon</div>
                        <div className="flex-1 p-2.5 flex justify-center items-center gap-2.5 overflow-hidden">
                            <div className="justify-start text-stone-500 text-sm font-bold font-['Be_Vietnam_Pro'] leading-tight">{user.phoneNumber}</div>
                        </div>
                        <div data-svg-wrapper className="relative">
                            <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M8.025 22.5L6.25 20.725L14.475 12.5L6.25 4.275L8.025 2.5L18.025 12.5L8.025 22.5Z" fill="black" />
                            </svg>
                        </div>

                        <M3ripple />
                    </div>
                </div>
                <div className="self-stretch rounded-lg outline outline-1 outline-offset-[-1px] outline-zinc-100 flex flex-col justify-center items-start">
                    <div className="self-stretch px-7 py-6 inline-flex justify-start items-center gap-2.5">
                        <div className="w-52 justify-start text-zinc-800 text-2xl font-semibold font-['Be_Vietnam_Pro'] leading-9">Ganti password</div>
                    </div>
                    <div className="self-stretch p-4 border-t border-zinc-100 flex flex-col justify-center items-start gap-2.5 overflow-hidden">
                        <div className="self-stretch justify-start text-stone-500 text-base font-bold font-['Be_Vietnam_Pro'] leading-normal">Anda bisa mengganti password anda disini jika anda perlu</div>
                        <div className="size- px-3.5 py-3 bg-zinc-600 rounded-md inline-flex justify-start items-start gap-1 relative">
                            <div className="justify-start text-white text-lg font-normal font-['Be_Vietnam_Pro']">Ganti</div>

                            <M3ripple />
                        </div>
                    </div>
                </div>
                <div className="self-stretch rounded-lg outline outline-1 outline-offset-[-1px] outline-zinc-100 inline-flex flex-col justify-center items-start">
                    <div className="self-stretch px-7 py-6 inline-flex justify-start items-center gap-2.5">
                        <div className="w-52 justify-start text-zinc-800 text-2xl font-semibold font-['Be_Vietnam_Pro'] leading-9">Keluar</div>
                    </div>
                    <div className="self-stretch p-4 border-t border-zinc-100 flex flex-col justify-center items-start gap-2.5 overflow-hidden">
                        <div className="self-stretch justify-start text-stone-500 text-base font-bold font-['Be_Vietnam_Pro'] leading-normal">Anda bisa keluar untuk ganti akun</div>
                        <button className="size- px-3.5 py-3 bg-red-400 rounded-md outline outline-1 outline-offset-[-1px] outline-red-600 inline-flex justify-start items-start gap-1 relative" onClick={() => {
                            dialogcontroller.showDialog(() => <SignOut />, [], true, "Keluar")
                        }}>
                            <div className="justify-start text-white text-lg font-normal font-['Be_Vietnam_Pro']">Keluar</div>
                            <M3ripple />
                        </button>
                    </div>
                </div>
            </div>;
        </main>
        <footer className="relative text-center flex justify-center items-start pt-6 pb-[120px]">
          <p className="w-full max-w-5xl md:max-w-6xl mx-auto px-4 md:px-6 text-gray-600 text-sm md:text-base font-medium">
            This applied research initiative is conducted by lecturers and students of Universitas Multimedia Nusantara, supported and funded through the 2025 Dikti Grant Scheme in collaboration with Kehati partners.
          </p>
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0 h-[120px] bg-bottom bg-repeat-x"
            style={{
              backgroundImage: `url(${new URL('~/src/assets/pattern.png', import.meta.url).href})`,
              backgroundSize: 'auto',
            }}
          />
        </footer>
    </div>
}

export function ProfilePage() {
    const authctx = useAuth();
    console.log(`/user/${authctx?.user?.uid}`)
    const dialogController = useDialogController();
    return <PageTemplate>
        <RoutedDBProvider query={`/user/${authctx?.user?.uid}`}>
            <DoneDataHandler dataHandler={(x: User | null, modify) => <ProfileContent dialogcontroller={dialogController} user={x} modify={modify} />} />
            <LoadingDataHandler dataHandler={() => <Loading />} />
            <ErrorDataHandler dataHandler={() => <Error />} />
        </RoutedDBProvider>
    </PageTemplate>
}